import { Controller } from "@hotwired/stimulus"

// Live preview for the admin cosmetic template form (epic #346, #351).
//
// User-explicit decision (2026-05-30): admins must see a rendered
// preview update in real time as they edit the form. Saving "blind"
// turns the workflow into trial-and-error. The preview mirrors the
// markup emitted by CosmeticRendererComponent (#354) so what the
// admin sees here is byte-for-byte what users see in prod.
//
// The controller is additive: if JS fails to boot, the server-side
// render bootstrap stays visible (graceful degradation). All inputs
// retain their data-actions so the template-form controller and form
// submission continue to work unaffected.
//
// Lottie playback reuses the existing `dotlottie-player` controller
// (#354, #381). We do not import the dotlottie runtime here — we just
// emit the same data-attributes that controller already listens for, so
// Stimulus auto-connects it (and lazy-imports the WASM) on its own.
//
// File uploads: `URL.createObjectURL(file)` gives the admin an
// instant local preview before the Active Storage direct upload
// finishes — no S3 round-trip required for the visual feedback.
// We track the blob URL so we can revoke it on the next change
// (avoids leaking object URLs across many edits).
//
// Accessibility: the embedded `dotlottie-player` controller already
// respects `prefers-reduced-motion`. For CSS-driven text effects
// (shimmer/glow/pulse) the cosmetic-renderer stylesheet itself
// disables keyframe animation under reduced-motion (see
// `web/app/stylesheets/application.tailwind.css`), so no extra
// guard is needed here.
export default class extends Controller {
  static targets = ["mount", "avatar"]

  static values = {
    initialKind: { type: String, default: "frame" },
    initialFormat: { type: String, default: "png" },
    initialName: { type: String, default: "" },
    initialAssetUrl: { type: String, default: "" },
    initialMetadata: { type: String, default: "{}" },
  }

  // Maps the model `kind` enum to the CosmeticRendererComponent slot
  // name. Same mapping the form's server bootstrap uses; kept here so
  // the client can re-pick a slot when the kind select changes
  // without a round-trip.
  static SLOT_BY_KIND = {
    frame: "avatar-frame",
    title: "title",
    card_bg: "card-bg",
    template: "profile-template",
  }

  // Allow-listed text styles, mirrors CosmeticRendererComponent
  // ALLOWED_TEXT_STYLES. Anything else falls back to "solid" so a
  // garbled metadata field cannot inject arbitrary CSS classes.
  static ALLOWED_TEXT_STYLES = ["solid", "gradient", "shimmer", "glow", "pulse"]

  connect() {
    this._blobUrl = null

    // Seed internal state from initial values so the first user-driven
    // refresh has a complete payload even before they touched some
    // fields.
    this.state = {
      kind: this.initialKindValue || "frame",
      format: this.initialFormatValue || "png",
      name: this.initialNameValue || "",
      assetUrl: this.initialAssetUrlValue || "",
      metadata: this.#safeParseJson(this.initialMetadataValue),
    }

    // Skip the first render: the server already painted the mount
    // (see _form.html.erb) so the page is meaningful pre-JS. We only
    // take over once the user actually changes something — keeps the
    // perceived load smoother.
  }

  disconnect() {
    this.#revokeBlobUrl()
  }

  // ---- INPUT HANDLERS ----

  nameChanged(event) {
    this.state.name = event.target.value
    this.#refresh()
  }

  kindChanged(event) {
    this.state.kind = event.target.value
    this.#refresh()
  }

  formatChanged(event) {
    this.state.format = event.target.value
    this.#refresh()
  }

  fileChanged(event) {
    const file = event.target.files && event.target.files[0]
    this.#revokeBlobUrl()
    if (file) {
      this._blobUrl = URL.createObjectURL(file)
      this.state.assetUrl = this._blobUrl
      // Derive the format from the SELECTED FILE, not the kind seed.
      // Since #463 `derived_asset_format_for` returns nil for asset-bearing
      // kinds (frame/card_bg/template), so `state.format` defaults to "png"
      // and a locally-selected `.lottie` fell into the <img> branch →
      // broken-image icon (#618). The renderer switches on the BYTES, so the
      // preview must too: inspect the file and route lottie → player,
      // raster/svg → <img>.
      this.state.format = this.#formatForFile(file)
    } else {
      this.state.assetUrl = this.initialAssetUrlValue || ""
      this.state.format = this.initialFormatValue || "png"
    }
    this.#refresh()
  }

  // Generic "something in the metadata changed" — re-reads the hidden
  // field which the template-form controller keeps in sync. We piggyback
  // on that single source of truth instead of duplicating the per-widget
  // reads here.
  metadataChanged() {
    const hidden = this.element.querySelector(
      'input[type=hidden][name="cosmetic_template[metadata]"]'
    )
    if (hidden) {
      this.state.metadata = this.#safeParseJson(hidden.value)
    }
    this.#refresh()
  }

  // ---- COMPOSER BRIDGE (#378 Bug 2) ----
  //
  // The composer controller publishes a window-scoped CustomEvent
  // every time the operator tweaks a widget. Mirroring it here lets
  // the sidebar (which lives in a sibling DOM column) reflect the
  // same Lottie the composer just generated, without coupling the
  // two controllers via a shared ancestor.
  //
  // Defence: any script on the page can dispatch this event — we
  // validate the payload before mutating state. blobUrl must be a
  // `blob:` URL (URL.createObjectURL output); format is allow-listed.
  // Failure modes degrade to clearing the preview, never to rendering
  // a hostile asset URL.
  composerUpdated(event) {
    const detail = (event && event.detail) || {}
    const format = String(detail.format || "")

    if (format === "cleared") {
      // Composer left the stage — drop the blob-driven state and
      // re-render from whatever the form natively carries (uploaded
      // file, asset_url, server bootstrap).
      this.#revokeBlobUrl()
      this.state.assetUrl = this.initialAssetUrlValue || ""
      this.state.format = this.#readFormatSelect() || this.state.format
      this.#refresh()
      return
    }

    if (format !== "lottie") return
    if (!this.#isSafeBlobUrl(detail.blobUrl)) return

    // The composer owns the lifecycle of its blob URL; we only borrow
    // the reference. Do NOT revoke it here — composer.revokeBlobUrl()
    // handles that on the next refresh / disconnect.
    this.state.assetUrl = detail.blobUrl
    this.state.format = "lottie"
    // Mirror animation_speed if the composer reported it so the
    // rendered preview matches the widget readout.
    const speed = Number(detail.animationSpeed)
    if (Number.isFinite(speed) && speed > 0) {
      this.state.metadata = { ...(this.state.metadata || {}), animation_speed: speed, loop: true }
    }
    this.#refresh()
  }

  // ---- HELPERS for the composer bridge ----

  #isSafeBlobUrl(value) {
    return typeof value === "string" && value.startsWith("blob:")
  }

  #readFormatSelect() {
    const sel = this.element.querySelector(
      'select[name="cosmetic_template[asset_format]"]'
    )
    return sel ? sel.value : null
  }

  // ---- RENDER ----

  #refresh() {
    if (!this.hasMountTarget) return

    const node = this.#buildNode()
    this.mountTarget.replaceChildren(node)

    // Hide the mock avatar when the cosmetic is a card_bg / template /
    // title — those slots do not sit on top of an avatar in prod.
    if (this.hasAvatarTarget) {
      const usesAvatar = this.state.kind === "frame"
      this.avatarTarget.classList.toggle("hidden", !usesAvatar)
    }
  }

  #buildNode() {
    const slot = this.constructor.SLOT_BY_KIND[this.state.kind] || "avatar-frame"
    const format = this.state.format
    const meta = this.state.metadata || {}

    if (format === "text") {
      return this.#buildText(slot, meta)
    }

    if (!this.state.assetUrl) {
      return this.#buildEmpty(slot)
    }

    if (format === "lottie") {
      return this.#buildLottie(slot, meta)
    }

    // png / webp / svg — plain <img>.
    return this.#buildImage(slot)
  }

  #buildEmpty(slot) {
    const span = document.createElement("span")
    span.className = `cosmetic-renderer cosmetic-renderer--${slot} cosmetic-renderer--empty`
    span.setAttribute("aria-hidden", "true")
    this.#applySize(span, slot)
    return span
  }

  #buildImage(slot) {
    const img = document.createElement("img")
    img.src = this.state.assetUrl
    img.alt = this.state.name || ""
    img.loading = "lazy"
    img.decoding = "async"
    img.className = `cosmetic-renderer cosmetic-renderer--${slot}`
    this.#applySize(img, slot)
    return img
  }

  #buildLottie(slot, meta) {
    const span = document.createElement("span")
    span.className = `cosmetic-renderer cosmetic-renderer--${slot}`
    span.setAttribute("role", "img")
    span.setAttribute("aria-label", this.state.name || "")
    this.#applySize(span, slot)

    // Hand off to the dotlottie-player Stimulus controller. It
    // lazy-imports the dotlottie-web WASM runtime only when an element
    // with this controller attaches, so we pay the runtime cost only on
    // the first lottie preview.
    span.setAttribute("data-controller", "dotlottie-player")
    span.setAttribute("data-dotlottie-player-src-value", this.state.assetUrl)
    const loop = meta.loop !== false
    span.setAttribute("data-dotlottie-player-loop-value", String(loop))
    const speed = Number(meta.animation_speed)
    if (Number.isFinite(speed) && speed > 0) {
      span.setAttribute("data-dotlottie-player-speed-value", String(speed))
    }
    return span
  }

  #buildText(slot, meta) {
    const span = document.createElement("span")
    const rawStyle = String(meta.text_style || "solid")
    const style = this.constructor.ALLOWED_TEXT_STYLES.includes(rawStyle)
      ? rawStyle
      : "solid"
    span.className = `cosmetic-title cosmetic-title--${style}`

    // CSS variables — sanitised hex only. Mirrors the server-side
    // HEX_COLOR_RE in CosmeticRendererComponent. Garbage values are
    // dropped rather than escaped, since the CSS interpolation is the
    // injection vector and rejecting unsafe input is safer than
    // attempting to neutralise it.
    const styleParts = []
    const from = this.#sanitiseColor(meta.color_start)
    const to = this.#sanitiseColor(meta.color_end)
    const single = this.#sanitiseColor(meta.color)
    if (from) styleParts.push(`--cosmetic-color-start:${from}`)
    if (to) styleParts.push(`--cosmetic-color-end:${to}`)
    if (single) styleParts.push(`--cosmetic-color:${single}`)
    if (styleParts.length) span.setAttribute("style", styleParts.join(";"))

    span.textContent = this.state.name || ""
    return span
  }

  // ---- HELPERS ----

  // Slot-aware sizing matches CosmeticRendererComponent::SIZES at :lg.
  // Hard-coded here because we cannot reach the Ruby constants from
  // the client; if those numbers change, update both sides.
  #applySize(el, slot) {
    const sizes = {
      "avatar-frame":     { w: 96, h: 96 },
      title:              { w: null, h: null },
      "card-bg":          { w: 280, h: 92 },
      "profile-template": { w: 320, h: 120 },
    }
    const dims = sizes[slot]
    if (!dims) return
    if (dims.w) el.style.width = `${dims.w}px`
    if (dims.h) el.style.height = `${dims.h}px`
  }

  // Map a locally-selected File to a renderer `asset_format`, mirroring the
  // server matrix CosmeticTemplate::ASSET_FORMAT_BY_CONTENT_TYPE. The
  // `.lottie` dotLottie ZIP reports `application/octet-stream` (or empty)
  // because the browser does not recognise the extension, so the filename
  // is authoritative for it. Lottie JSON, SVG and WebP carry honest MIME
  // types; anything else falls back to the static-raster `png` sentinel
  // (the <img> branch).
  #formatForFile(file) {
    const name = String(file.name || "").toLowerCase()
    const type = String(file.type || "").toLowerCase()
    if (name.endsWith(".lottie")) return "lottie"
    if (type === "application/zip") return "lottie"
    if (type === "application/json" || name.endsWith(".json")) return "lottie"
    if (type === "image/svg+xml" || name.endsWith(".svg")) return "svg"
    if (type === "image/webp" || name.endsWith(".webp")) return "webp"
    return "png"
  }

  #safeParseJson(raw) {
    try {
      const v = JSON.parse(raw || "{}")
      return v && typeof v === "object" ? v : {}
    } catch (_e) {
      return {}
    }
  }

  #sanitiseColor(value) {
    const str = String(value || "").trim()
    return /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(str)
      ? str
      : null
  }

  #revokeBlobUrl() {
    if (this._blobUrl) {
      try { URL.revokeObjectURL(this._blobUrl) } catch (_e) { /* noop */ }
      this._blobUrl = null
    }
  }
}
